import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { roleApi } from "./useRoleApi";
import { ROLE_ERROR_MESSAGES } from "../constants";
import queryKeys from "../../../lib/query/queryKeys";

export const useRoleManagement = () => {
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState(null);

  const {
    data: roles = [],
    isLoading: loading,
    error: queryError,
    refetch: fetchRoles,
  } = useQuery({
    queryKey: queryKeys.roles.list(),
    queryFn: () => roleApi.getAllRoles(),
    staleTime: 2 * 60 * 1000,
  });

  const error = queryError ? queryError.response?.data?.["error-message"] || ROLE_ERROR_MESSAGES.FETCH_ROLES : null;

  const createMutation = useMutation({
    mutationFn: roleData => roleApi.createRole(roleData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ roleId, roleData }) => roleApi.updateRole(roleId, roleData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: roleId => roleApi.deleteRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
    },
  });

  const createRole = useCallback(
    async roleData => {
      return createMutation.mutateAsync(roleData);
    },
    [createMutation]
  );

  const updateRole = useCallback(
    async (roleId, roleData) => {
      return updateMutation.mutateAsync({ roleId, roleData });
    },
    [updateMutation]
  );

  const deleteRole = useCallback(
    async roleId => {
      return deleteMutation.mutateAsync(roleId);
    },
    [deleteMutation]
  );

  const getRolePermissions = useCallback(async roleId => {
    return roleApi.getRolePermissions(roleId);
  }, []);

  const selectRole = useCallback(role => {
    setSelectedRole(role);
  }, []);

  const clearError = useCallback(() => {
    // React Query manages error state automatically
  }, []);

  const resetState = useCallback(() => {
    setSelectedRole(null);
    queryClient.removeQueries({ queryKey: queryKeys.roles.all });
  }, [queryClient]);

  return {
    roles,
    loading: loading || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    error,
    selectedRole,
    fetchRoles,
    createRole,
    updateRole,
    deleteRole,
    getRolePermissions,
    selectRole,
    clearError,
    resetState,
  };
};
